import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChefHat, FileSpreadsheet, FileText, History, PackagePlus, Trash2 } from "lucide-react";
import { API, downloadBlob } from "../../lib/apiClient";
import { Field } from "./Shared";

const localToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const emptyForm = () => ({ date: localToday(), item_id: "", quantity_issued: "", issued_to: "", remarks: "" });
const formatLocalDate = (isoDate) => { const [y, m, d] = isoDate.slice(0, 10).split("-").map(Number); return new Date(y, m - 1, d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); };

function rangeQuery(range) { const parts = []; if (range.start) parts.push(`start_date=${range.start}`); if (range.end) parts.push(`end_date=${range.end}`); return parts.length ? `&${parts.join("&")}` : ""; }

export function KitchenRequisitionsView({ rawMaterialItems, requisitions, onSaved, canEdit, onDelete }) {
  const [form, setForm] = useState(emptyForm);
  const [range, setRange] = useState({ start: "", end: "" });
  const today = localToday();
  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const selectedItem = rawMaterialItems.find((i) => i.id === form.item_id);
  const visibleRequisitions = requisitions.filter((r) => {
    const d = r.date.slice(0, 10);
    if (range.start && d < range.start) return false;
    if (range.end && d > range.end) return false;
    return true;
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/requisitions`, { ...form, quantity_issued: Number(form.quantity_issued) });
      toast.success("Requisition logged and stock deducted");
      setForm(emptyForm());
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not log requisition"); }
  };

  return <div className="page-wrap">
    <div className="page-heading"><div><p className="eyebrow">Daily operations</p><h1>Kitchen Usage</h1><p className="subheading">Log raw material transfers from the store to the kitchen for cooking.</p></div></div>

    <section className="panel" style={{ marginBottom: 24 }} data-testid="requisition-form-panel">
      <div className="panel-heading"><div><h2>Log a requisition</h2><p>Deducts the issued quantity from raw material stock immediately</p></div><ChefHat size={21} className="muted-icon" /></div>
      <form onSubmit={submit} className="settings-form">
        <div className="form-grid">
          <Field label="Date" name="date" type="date" value={form.date} onChange={update} required testid="requisition-date-input" />
          <label>Item (raw material)
            <select data-testid="requisition-item-select" value={form.item_id} onChange={(e) => setForm({ ...form, item_id: e.target.value })} required>
              <option value="">— Select item —</option>
              {rawMaterialItems.map((i) => <option key={i.id} value={i.id}>{i.name} · {i.quantity} {i.unit} available</option>)}
            </select>
          </label>
          <Field label={`Quantity issued${selectedItem ? ` (${selectedItem.unit})` : ""}`} name="quantity_issued" type="number" step="0.01" min="0.01" value={form.quantity_issued} onChange={update} required testid="requisition-quantity-input" />
          <Field label="Issued to (Chef / Kitchen section)" name="issued_to" value={form.issued_to} onChange={update} required testid="requisition-issued-to-input" placeholder="e.g. Chef Ramesh / Main Kitchen" />
        </div>
        <label>Remarks (optional)
          <textarea data-testid="requisition-remarks-input" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="e.g. Used for lunch service" />
        </label>
        <button type="submit" className="button primary" data-testid="requisition-submit-button"><PackagePlus size={16} /> Log requisition</button>
      </form>
    </section>

    <section className="panel report-card" data-testid="requisition-history-panel">
      <div className="panel-heading"><div><h2>Requisition history</h2><p>{visibleRequisitions.length} {visibleRequisitions.length === 1 ? "entry" : "entries"} logged</p></div><History size={21} className="muted-icon" /></div>
      <div className="report-range">
        <label>From<input type="date" data-testid="requisition-history-start-input" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} /></label>
        <label>To<input type="date" data-testid="requisition-history-end-input" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} /></label>
      </div>
      <div className="report-actions">
        <button className="button ghost" data-testid="requisition-export-excel-button" onClick={() => downloadBlob(`${API}/reports/kitchen-requisitions?format=excel${rangeQuery(range)}`, `kitchen_requisitions_${today}.xlsx`)}><FileSpreadsheet size={15} /> Excel</button>
        <button className="button ghost" data-testid="requisition-export-pdf-button" onClick={() => downloadBlob(`${API}/reports/kitchen-requisitions?format=pdf${rangeQuery(range)}`, `kitchen_requisitions_${today}.pdf`)}><FileText size={15} /> PDF</button>
      </div>
      <div className="table-panel">
        <table data-testid="requisition-table">
          <thead><tr><th>Req no.</th><th>Date</th><th>Item</th><th>Qty issued</th><th>Issued to</th><th>Remarks</th><th>Logged by</th><th><span className="sr-only">Actions</span></th></tr></thead>
          <tbody>{visibleRequisitions.map((r) => <tr key={r.id}>
            <td><b className="challan-number" data-testid={`requisition-no-${r.id}`}>{r.req_no}</b></td>
            <td>{formatLocalDate(r.date)}</td>
            <td>{r.item_name}</td>
            <td>{r.quantity_issued} {r.unit}</td>
            <td>{r.issued_to}</td>
            <td>{r.remarks || "—"}</td>
            <td>{r.created_by}</td>
            <td><div className="row-actions">{canEdit && <button data-testid={`requisition-delete-${r.id}`} title="Delete and restock" className="danger-action" onClick={() => onDelete(r)}><Trash2 size={15} /></button>}</div></td>
          </tr>)}</tbody>
        </table>
        {visibleRequisitions.length === 0 && <div className="empty table-empty" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}><ChefHat size={27} /><p>No kitchen requisitions logged in this range.</p></div>}
      </div>
    </section>
  </div>;
}
